// ==========================================================================
// Auth Store - Token management and initial login flow
// User data is managed by React Query (useUser hook)
// ==========================================================================

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { FSP } from '@/types/user';
import {
  createUser,
  verifyMagicLink,
  extractTokenFromMagicLink,
  generateRandomPhone,
} from '@/services/user.service';
import i18n from '@/i18n';
import { resetMixpanel } from '@/lib/mixpanel';

interface AuthState {
  // State
  pendingMagicLinkToken: string | null;
  isLoading: boolean;
  error: string | null;
  
  // Actions
  createTestUser: (withLocation: boolean, phoneNumber?: string) => Promise<void>;
  verifyAndLogin: (token?: string) => Promise<void>;
  logout: () => void;
  clearError: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      pendingMagicLinkToken: null,
      isLoading: false,
      error: null,

      // Create a test user for development (with or without location)
      createTestUser: async (withLocation: boolean, phoneNumber?: string) => {
        set({ isLoading: true, error: null });
        
        try {
          const payload = {
            fsp_id: 1,
            name: 'John Doe',
            phone_number: phoneNumber || generateRandomPhone(),
            ...(withLocation && {
              location: {
                lat: -1.2921,
                long: 36.8219,
              },
            }),
          };
          
          const response = await createUser(payload);

          // Extract token from magic link
          const token = extractTokenFromMagicLink(response.magic_link);
          
          set({
            pendingMagicLinkToken: token,
            isLoading: false,
          });
          
          // Update locale
          if (response.data.locale) {
            i18n.changeLanguage(response.data.locale);
          }
          
        } catch (error) {
          // Toast shown by axios interceptor
          set({
            error: error instanceof Error ? error.message : 'Failed to create user',
            isLoading: false,
          });
          throw error;
        }
      },

      // Verify magic link and get access token
      verifyAndLogin: async (token?: string) => {
        const magicToken = token || get().pendingMagicLinkToken;
        
        if (!magicToken) {
          set({ error: 'No magic link token available' });
          throw new Error('No magic link token');
        }
        
        set({ isLoading: true, error: null });
        
        try {
          const response = await verifyMagicLink(magicToken);
          
          // Store token in localStorage
          localStorage.setItem('access_token', response.access_token);
          
          set({
            pendingMagicLinkToken: null,
            isLoading: false,
          });
          
          // Update locale
          if (response.user.locale) {
            i18n.changeLanguage(response.user.locale);
          }
          
          // Apply FSP branding immediately
          if (response.user.fsp) {
            applyFSPBranding(response.user.fsp);
          }
          
        } catch (error) {
          // Toast shown by axios interceptor
          set({
            error: error instanceof Error ? error.message : 'Failed to verify',
            isLoading: false,
          });
          throw error;
        }
      },

      // Logout - clear token and reset
      logout: () => {
        localStorage.removeItem('access_token');
        resetBranding();
        resetMixpanel();
        set({
          pendingMagicLinkToken: null,
          error: null,
        });
      },
      
      clearError: () => set({ error: null }),
    }),
    {
      name: 'yaya-auth',
      partialize: (state) => ({
        pendingMagicLinkToken: state.pendingMagicLinkToken,
      }),
    }
  )
);

// Default brand colors (fallback)
const DEFAULT_PRIMARY = '#ED974F';
const DEFAULT_SECONDARY = '#c47535';

// Track active blob URL for manifest cleanup
let activeBlobUrl: string | null = null;

/**
 * Determine app name based on FSP.
 * Atram FSP → "Atram", all others → FSP name
 */
function getAppName(fsp: FSP): string {
  const isAtram = fsp.slug?.toLowerCase().includes('atram') || fsp.name?.toLowerCase().includes('atram');
  return isAtram ? 'Atram' : (fsp.name || 'Weather');
}

/** Check if FSP is Atram */
function isAtramFSP(fsp: FSP): boolean {
  return !!(fsp.slug?.toLowerCase().includes('atram') || fsp.name?.toLowerCase().includes('atram'));
}

/**
 * Update PWA manifest dynamically with FSP icon and name.
 * Creates a blob manifest and swaps the <link rel="manifest"> href.
 */
function updatePWAManifest(opts: { name: string; iconUrl: string; themeColor: string }) {
  const manifest = {
    name: opts.name,
    short_name: opts.name,
    display: 'standalone',
    orientation: 'portrait-primary',
    scope: '/',
    start_url: '/',
    theme_color: opts.themeColor,
    background_color: '#ffffff',
    icons: [
      { src: opts.iconUrl, sizes: '192x192', type: 'image/png' },
      { src: opts.iconUrl, sizes: '512x512', type: 'image/png' },
      { src: opts.iconUrl, sizes: '512x512', type: 'image/png', purpose: 'maskable' },
    ],
  };

  // Clean up previous blob URL
  if (activeBlobUrl) URL.revokeObjectURL(activeBlobUrl);

  const blob = new Blob([JSON.stringify(manifest)], { type: 'application/manifest+json' });
  activeBlobUrl = URL.createObjectURL(blob);

  let manifestLink = document.querySelector<HTMLLinkElement>('link[rel="manifest"]');
  if (!manifestLink) {
    manifestLink = document.createElement('link');
    manifestLink.rel = 'manifest';
    document.head.appendChild(manifestLink);
  }
  manifestLink.href = activeBlobUrl;
}

/**
 * Apply FSP branding (colors, favicon, logo, manifest, title)
 */
export function applyFSPBranding(fsp: FSP) {
  const root = document.documentElement;

  // Set CSS variables for colors
  root.style.setProperty('--brand-primary', fsp.primary_color || DEFAULT_PRIMARY);
  root.style.setProperty('--brand-secondary', fsp.secondary_color || DEFAULT_SECONDARY);
  root.style.setProperty('--color-ring', fsp.primary_color || DEFAULT_PRIMARY);
  root.style.setProperty('--brand-gradient-start', fsp.primary_color || DEFAULT_PRIMARY);
  root.style.setProperty('--brand-gradient-end', fsp.secondary_color || DEFAULT_SECONDARY);

  // Update favicon
  const favicon = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
  if (favicon && fsp.favIcon) {
    favicon.href = fsp.favIcon;
  }

  // Update apple touch icon
  const appleTouchIcon = document.querySelector<HTMLLinkElement>('link[rel="apple-touch-icon"]');
  if (appleTouchIcon && fsp.appIcon) {
    appleTouchIcon.href = fsp.appIcon;
  }

  // Update theme color
  const themeColor = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
  if (themeColor) {
    themeColor.content = fsp.primary_color || DEFAULT_PRIMARY;
  }

  // Determine app name: Atram → "Atram", others → "Weather"
  const appName = getAppName(fsp);

  // Update document title
  document.title = appName;

  // Update apple-mobile-web-app-title
  const appTitleMeta = document.querySelector<HTMLMetaElement>('meta[name="apple-mobile-web-app-title"]');
  if (appTitleMeta) {
    appTitleMeta.content = appName;
  }

  // Update PWA manifest with FSP icon and name
  // Atram uses local icon, other FSPs use their appIcon from API
  const iconUrl = isAtramFSP(fsp) ? '/atram_icon.png' : (fsp.appIcon || '/atram_icon.png');
  updatePWAManifest({
    name: appName,
    iconUrl,
    themeColor: fsp.primary_color || DEFAULT_PRIMARY,
  });
}

/**
 * Reset branding to defaults
 */
export function resetBranding() {
  const root = document.documentElement;

  root.style.setProperty('--brand-primary', DEFAULT_PRIMARY);
  root.style.setProperty('--brand-secondary', DEFAULT_SECONDARY);
  root.style.setProperty('--color-ring', DEFAULT_PRIMARY);
  root.style.setProperty('--brand-gradient-start', DEFAULT_PRIMARY);
  root.style.setProperty('--brand-gradient-end', DEFAULT_SECONDARY);

  document.title = 'Atram';

  // Restore default favicon and apple-touch-icon
  const favicon = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
  if (favicon) favicon.href = '/favicon.png';
  const appleTouchIcon = document.querySelector<HTMLLinkElement>('link[rel="apple-touch-icon"]');
  if (appleTouchIcon) appleTouchIcon.href = '/apple-touch-icon.png';

  // Restore default manifest
  if (activeBlobUrl) {
    URL.revokeObjectURL(activeBlobUrl);
    activeBlobUrl = null;
  }
  const manifestLink = document.querySelector<HTMLLinkElement>('link[rel="manifest"]');
  if (manifestLink) manifestLink.href = '/manifest.webmanifest';
}
